import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../assets/images/logo.png";

export function useDoubleTickWidget() {
  const location = useLocation();
  const isShowSetupMenu = useSelector((state) => state.auth?.isShowSetupMenu) || false;

  useEffect(() => {
    // Only show widget on registration pages, business dashboard, or when setup dialog is open
    const isRegistrationPage = location.pathname.includes('/register') ||
                               location.pathname.includes('/business/get-started');
    const isBusinessDashboard = location.pathname.includes('/business-dashboard');
    const shouldShowWidget = isRegistrationPage || isBusinessDashboard || isShowSetupMenu;

    // Shared helper — uses setProperty("important") so the SDK cannot override it
    const applyVisibility = (visible) => {
      const widgetSelectors = [
        'iframe[src*="doubletick"]',
        'iframe[src*="d3r49s2alut4u1"]',
        'div[id*="dt-widget"]',
        'div[class*="dt-widget"]',
        '[data-dt-widget]',
        '#dt-widget',
        '.dt-widget'
      ];
      widgetSelectors.forEach(selector => {
        try {
          // querySelectorAll — target ALL matching elements, not just the first
          document.querySelectorAll(selector).forEach(el => {
            el.style.setProperty('display', visible ? 'block' : 'none', 'important');
            el.style.setProperty('visibility', visible ? 'visible' : 'hidden', 'important');
            el.style.setProperty('opacity', visible ? '1' : '0', 'important');
            el.style.setProperty('pointer-events', visible ? 'auto' : 'none', 'important');
          });
        } catch (e) { /* ignore */ }
      });
      document.querySelectorAll('iframe').forEach(iframe => {
        if (iframe.src && (iframe.src.includes('doubletick') || iframe.src.includes('d3r49s2alut4u1'))) {
          iframe.style.setProperty('display', visible ? 'block' : 'none', 'important');
          iframe.style.setProperty('visibility', visible ? 'visible' : 'hidden', 'important');
          iframe.style.setProperty('opacity', visible ? '1' : '0', 'important');
          iframe.style.setProperty('pointer-events', visible ? 'auto' : 'none', 'important');
        }
      });
    };

    // Hide widget if not on an allowed page
    if (!shouldShowWidget && window.dt) {
      applyVisibility(false);
      // Re-hide at multiple delays — SDK may re-show itself via setTimeout/rAF internally
      const t1 = setTimeout(() => applyVisibility(false), 200);
      const t2 = setTimeout(() => applyVisibility(false), 600);
      const t3 = setTimeout(() => applyVisibility(false), 1500);
      // Watch for BOTH new elements (childList) AND style-attribute changes (attributes)
      // The SDK typically re-shows by setting style.display on an existing element,
      // which is an attribute mutation — childList alone won't catch it
      const observer = new MutationObserver(() => applyVisibility(false));
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        observer.disconnect();
      };
    }

    // Only initialize on allowed pages
    if (!shouldShowWidget) return;

    // Already loaded — just make it visible
    if (window.dt) {
      applyVisibility(true);
      return;
    }

    (function (w, d, s, o, f) {
      w[o] =
        w[o] ||
        function () {
          (w[o].q = w[o].q || []).push(arguments);
        };

      const js = d.createElement(s);
      const fjs = d.getElementsByTagName(s)[0];
      js.id = o;
      js.src = f;
      js.async = true;
      fjs.parentNode.insertBefore(js, fjs);
    })(
      window,
      document,
      "script",
      "dt",
      "https://d3r49s2alut4u1.cloudfront.net/js/widget.js"
    );

    // Init AFTER script load (queued safely)
    window.dt("init", {
      crmWidgetId: "53ecd9d6-be5e-4427-9d0a-3b8a1787dcf8",
      companyName: "beemz",
      companyLogoUrl: logo,
      phoneNumber: "918105405606",
    });
  }, [location.pathname, isShowSetupMenu]);
}
